import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import { useMpgList } from '../../../lib/mpg-api';
import { useMssList } from '../../../lib/mss-api';
import { useMsfList } from '../../../lib/msf-api';
import { useInvList } from '../../../lib/inv-api';
import { useSapList } from '../../../lib/sap-api';

type DocRow = { id: string; type: string; label: string; workDate?: string | null };

function Section({ title, rows, hrefBase }: { title: string; rows: DocRow[]; hrefBase: string }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        {title} <span className="text-slate-400">({rows.length})</span>
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-400">{t('dashboard.docOpenOther.none')}</p>
      ) : (
        <ul className="space-y-0.5 text-xs">
          {rows.slice(0, 5).map((r) => (
            <li key={r.id}>
              <Link to={`${hrefBase}/${r.id}/edit`} className="text-brand-700 hover:underline">
                {r.label}
              </Link>
              {r.workDate && (
                <span className="ml-2 text-slate-400">
                  {new Date(r.workDate).toLocaleDateString('it-IT')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DocOpenOtherWidget() {
  const { t } = useTranslation();
  const mpgQ = useMpgList({ status: 'DRAFT', pageSize: 20 });
  const mssQ = useMssList({ status: 'DRAFT', pageSize: 20 });
  const msfQ = useMsfList({ status: 'DRAFT', pageSize: 20 });
  const invQ = useInvList({ status: 'DRAFT', pageSize: 20 });
  const sapQ = useSapList({ status: 'DRAFT', pageSize: 20 });

  const mpgRows: DocRow[] = (mpgQ.data?.items ?? []).map((d) => ({
    id: d.id,
    type: 'MPG',
    label: `MPG ${d.code ?? '—'}`,
    workDate: d.workDate,
  }));
  const mssRows: DocRow[] = (mssQ.data?.items ?? []).map((d) => ({
    id: d.id,
    type: 'MSS',
    label: `MSS ${d.code ?? '—'}`,
    workDate: d.workDate,
  }));
  const msfRows: DocRow[] = (msfQ.data?.items ?? []).map((d) => ({
    id: d.id,
    type: 'MSF',
    label: `MSF ${d.code ?? '—'}`,
    workDate: d.workDate,
  }));
  const invRows: DocRow[] = (invQ.data?.items ?? []).map((d) => ({
    id: d.id,
    type: 'INV',
    label: `INV ${d.code ?? '—'}`,
    workDate: d.workDate,
  }));
  const sapRows: DocRow[] = (sapQ.data?.items ?? []).map((d) => ({
    id: d.id,
    type: 'SAP',
    label: `SAP ${d.code ?? d.draftName ?? '—'}`,
    workDate: d.workDate,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.docOpenOther.title')}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4">
        <Section title="SAP" rows={sapRows} hrefBase="/documents/sap" />
        <Section title="MPG" rows={mpgRows} hrefBase="/documents/mpg" />
        <Section title="MSS" rows={mssRows} hrefBase="/documents/mss" />
        <Section title="MSF" rows={msfRows} hrefBase="/documents/msf" />
        <Section title="INV" rows={invRows} hrefBase="/documents/inv" />
      </CardContent>
    </Card>
  );
}
